import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { useViewport } from '@/hooks';
import { DialogBody, DialogHeader, ResponsiveDialog, TooltipProvider } from '@/ui_v3';
import { useClassroomLinking } from '../hooks';
import { type ContentShareType } from '../types';
import { trackClassroomEvent } from '../utils';
import { ClassroomLinkCard } from './ClassroomLinkCard';
import { CreateClassroomButton } from './CreateClassroomButton';
interface ClassroomLinkModalProps {
  contentId: string;
  contentType: ContentShareType;
  onClose: () => void;
}
function ClassroomLinkModal({
  contentId,
  contentType,
  onClose
}: ClassroomLinkModalProps) {
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  const {
    classrooms,
    isLoadingClassrooms,
    isPending,
    linkClassroom,
    unlinkClassroom
  } = useClassroomLinking({
    contentId,
    contentType
  });
  let classroomListContent: ReactNode;
  if (isLoadingClassrooms) {
    classroomListContent = null;
  } else if (classrooms.length === 0) {
    classroomListContent = <p className="py-lg text-center text-body-sm-medium text-alternative">
        {t('common:classroomLink.emptyMessage')}
      </p>;
  } else {
    classroomListContent = classrooms.map(classroom => <ClassroomLinkCard key={classroom.classGroupId} classroom={classroom} isPending={isPending} isLinked={classroom.isLinked ?? false} linkedClassLinkId={classroom.linkedClassLinkId} onLink={linkClassroom} onUnlink={unlinkClassroom} />);
  }
  return <ResponsiveDialog size={isMobile ? 'lg' : 'md'} onClose={onClose} className="mobile-lg:!h-[480px]" data-sentry-element="ResponsiveDialog" data-sentry-component="ClassroomLinkModal" data-sentry-source-file="ClassroomLinkModal.tsx">
      <DialogHeader title={t('common:classroomLink.modalTitle')} hasCloseButton={true} onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ClassroomLinkModal.tsx" />
      <DialogBody className="flex min-h-0 flex-1 flex-col overflow-hidden px-md pb-lg mobile-lg:px-lg" data-sentry-element="DialogBody" data-sentry-source-file="ClassroomLinkModal.tsx">
        <TooltipProvider delayDuration={0} data-sentry-element="TooltipProvider" data-sentry-source-file="ClassroomLinkModal.tsx">
          <div className="flex h-full min-h-0 flex-col">
            <CreateClassroomButton className="shrink-0" onBeforeOpenClassroom={() => trackClassroomEvent('click_create_classroom', {
            source: 'classroom_link_modal',
            contentId,
            contentType
          })} data-sentry-element="CreateClassroomButton" data-sentry-source-file="ClassroomLinkModal.tsx" />
            <div className="mt-sm flex min-h-0 flex-1 flex-wrap content-start gap-[4px] overflow-y-auto">
              {classroomListContent}
            </div>
          </div>
        </TooltipProvider>
      </DialogBody>
    </ResponsiveDialog>;
}
interface ShowClassroomLinkModalPayload {
  contentId: string;
  contentType: ContentShareType;
}
export function showClassroomLinkModal(payload: ShowClassroomLinkModalPayload) {
  trackClassroomEvent('view_classroom_link_modal', {
    source: 'classroom_link_modal',
    contentId: payload.contentId,
    contentType: payload.contentType
  });
  overlay.open(({
    unmount
  }) => <ClassroomLinkModal {...payload} onClose={unmount} />);
}